import { formatMoney, formatMoneyCompact } from '../../../utils/numberFormat';
import { formatDayMonth, formatDayMonthYear } from '../../../utils/dateFormat';

const SYMBOL: Record<string, string> = { EUR: '€', USD: '$', GBP: '£' };

function symbolFor(currency: string): string {
  return SYMBOL[currency] ?? `${currency} `;
}

/** Finance data in the OS starts here; charts and ranges never reach further back. */
export const FINANCE_START_MONTH = '2026-01';

/** Months from FINANCE_START_MONTH through the current month, inclusive. */
export function monthsSinceFinanceStart(now = new Date()): number {
  const [year, month] = FINANCE_START_MONTH.split('-').map(Number);
  return (now.getFullYear() - year!) * 12 + (now.getMonth() + 1 - month!) + 1;
}

/** "YYYY-MM" of the running month. */
export function currentMonthKey(now = new Date()): string {
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
}

/** True when the month key is the running month, which is only month-to-date. */
export function isCurrentMonth(monthKey: string, now = new Date()): boolean {
  return monthKey.slice(0, 7) === currentMonthKey(now);
}

/** Axis tick for a month: "Sep 26", and "Oct 26 MTD" for the running month. */
export function monthTick(monthKey: string): string {
  return isCurrentMonth(monthKey) ? `${formatMonthKey(monthKey)} MTD` : formatMonthKey(monthKey);
}

/** Tooltip heading for a month: "Oct 26 (month to date)" for the running month. */
export function monthLabel(monthKey: string): string {
  return isCurrentMonth(monthKey) ? `${formatMonthKey(monthKey)} (month to date)` : formatMonthKey(monthKey);
}

/** Opacity for a month's bars: the running month is faded, it is still filling up. */
export function monthOpacity(monthKey: string): number {
  return isCurrentMonth(monthKey) ? 0.4 : 1;
}

/**
 * A first bill after this day of the month means the books start mid-month,
 * so that first month only holds part of a month's spend.
 */
const PARTIAL_FIRST_MONTH_AFTER_DAY = 7;

/**
 * The months a monthly average may divide by: months with spend, minus the
 * running month (month-to-date) and minus a first month the books only cover
 * in part. Averaging over those read a half month as a whole one.
 */
export function fullMonths<T extends { month: string; total: number }>(
  series: T[],
  firstBillDate: string | null,
  now = new Date(),
): T[] {
  const withSpend = series.filter((p) => p.total !== 0 && !isCurrentMonth(p.month, now));
  const first = withSpend[0];
  if (
    first &&
    firstBillDate &&
    firstBillDate.slice(0, 7) === first.month &&
    Number(firstBillDate.slice(8, 10)) > PARTIAL_FIRST_MONTH_AFTER_DAY
  ) {
    return withSpend.slice(1);
  }
  return withSpend;
}

/** "Jan–Sep 26", or "Sep 26" for one month: the span an average covers. */
export function monthSpan(months: string[]): string {
  if (months.length === 0) return '';
  const first = months[0]!;
  const last = months[months.length - 1]!;
  if (first === last) return formatMonthKey(first);
  return first.slice(0, 4) === last.slice(0, 4)
    ? `${formatMonthKey(first).split(' ')[0]}–${formatMonthKey(last)}`
    : `${formatMonthKey(first)}–${formatMonthKey(last)}`;
}

/** True for a "YYYY-MM…" key on or after FINANCE_START_MONTH. */
export function inFinancePeriod(monthKey: string): boolean {
  return monthKey.slice(0, 7) >= FINANCE_START_MONTH;
}

/** Currency-aware money for the Finance pages, no decimals: "€80,282". */
export function money(value: number, currency = 'EUR'): string {
  return formatMoney(Math.round(value), symbolFor(currency));
}

/**
 * A term added to a sum in a sentence: "+ €4,490", or "− €30,000" for a
 * negative one, never "+ −€30,000".
 */
export function plusMoney(value: number, currency = 'EUR'): string {
  return Math.round(value) < 0 ? `− ${money(-value, currency)}` : `+ ${money(value, currency)}`;
}

/** Compact money for narrow tiles and axes: "€1.02M", "€80.3k". Pair with the full value in a title. */
export function moneyCompact(value: number, currency = 'EUR'): string {
  return formatMoneyCompact(value, symbolFor(currency));
}

/**
 * Money for chart axes: abbreviated from 1,000 so neighbouring ticks read alike
 * ("€5k" next to "€10k", not "€5,000"). Axes only; tiles keep moneyCompact.
 */
export function moneyAxis(value: number, currency = 'EUR'): string {
  const abs = Math.abs(value);
  if (abs < 1_000 || abs >= 1_000_000) return moneyCompact(value, currency);
  const sign = value < 0 ? '−' : '';
  return `${sign}${symbolFor(currency)}${Number((abs / 1_000).toFixed(1))}k`;
}

/** Signed money for movements: "+€3,000", "−€625". */
export function signedMoney(value: number, currency = 'EUR'): string {
  return `${value > 0 ? '+' : ''}${money(value, currency)}`;
}

function fromIsoDay(iso: string): Date {
  const [year, month, day] = iso.slice(0, 10).split('-').map(Number);
  return new Date(year, month - 1, day);
}

const MONTH_ABBR = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/**
 * "Sep 26" from a YYYY-MM(-DD) key. Built from a fixed list rather than
 * toLocaleDateString, whose en-GB short month is "Sept" on current ICU.
 */
export function formatMonthKey(key: string): string {
  const [year, month] = key.split('-');
  const abbr = MONTH_ABBR[Number(month) - 1];
  return abbr ? `${abbr} ${year.slice(-2)}` : key;
}

/**
 * Round axis ticks that fit the data: a 1/2/2.5/5 × 10ⁿ step and a domain that
 * starts and ends on a tick. Of the round steps that give roughly `target`
 * intervals, the one with the tightest span wins, so the top tick is the first
 * round step above the data rather than a whole step further.
 *
 * Zero is included by default so bars stand on it. Pass `includeZero: false`
 * for a line whose values sit far from zero (MRR between €62k and €80k), so the
 * axis starts near the data instead of flattening it.
 */
export function niceTicks(
  values: number[],
  target = 5,
  { includeZero = true }: { includeZero?: boolean } = {},
): { domain: [number, number]; ticks: number[] } {
  const finite = values.filter((v) => Number.isFinite(v));
  if (finite.length === 0) return { domain: [0, 1], ticks: [0, 1] };
  const lo = includeZero ? Math.min(0, ...finite) : Math.min(...finite);
  const hi = includeZero ? Math.max(0, ...finite) : Math.max(...finite);
  if (lo === hi) {
    if (lo === 0) return { domain: [0, 1], ticks: [0, 1] };
    // A flat line: one round step either side of the value.
    const step = 10 ** Math.floor(Math.log10(Math.abs(lo)));
    const min = Math.floor(lo / step) * step - (includeZero ? 0 : step);
    const max = Math.ceil(hi / step) * step + step;
    return niceTicks([min, max], target, { includeZero });
  }
  const raw = (hi - lo) / target;
  const baseMagnitude = 10 ** Math.floor(Math.log10(raw));
  let best: { step: number; min: number; max: number; n: number } | null = null;
  for (const magnitude of [baseMagnitude / 10, baseMagnitude, baseMagnitude * 10]) {
    for (const m of [1, 2, 2.5, 5]) {
      const step = m * magnitude;
      const min = Math.floor(lo / step) * step;
      const max = Math.ceil(hi / step) * step;
      const n = Math.round((max - min) / step);
      if (n < Math.max(2, target - 1) || n > target + 1) continue;
      const span = max - min;
      if (
        best === null ||
        span < best.max - best.min - 1e-9 ||
        (Math.abs(span - (best.max - best.min)) < 1e-9 && Math.abs(n - target) < Math.abs(best.n - target))
      ) {
        best = { step, min, max, n };
      }
    }
  }
  if (best === null) {
    // No round step lands in range (very lopsided data): fall back to the first
    // round step at or above the raw interval.
    const step = ([1, 2, 2.5, 5, 10].find((m) => m * baseMagnitude >= raw) ?? 10) * baseMagnitude;
    best = { step, min: Math.floor(lo / step) * step, max: Math.ceil(hi / step) * step, n: 0 };
  }
  const ticks: number[] = [];
  for (let t = best.min; t <= best.max + best.step / 2; t += best.step) ticks.push(Math.round(t * 100) / 100);
  return { domain: [best.min, best.max], ticks };
}

const SMALL_WORDS = new Set(['and', 'or', 'of', 'for', 'the', 'a', 'to', 'in', 'on', 'per']);
const KEEP_UPPER = new Set(['SEQUESTO', 'AI', 'API', 'CRM', 'SEO', 'SSO', 'EU', 'UK', 'US', 'IT', 'SaaS', 'MSA', 'RFP', 'ID']);

/** Title-case a label that arrived all caps or all lower; leave mixed case alone. */
function tidyCase(label: string): string {
  if (label !== label.toUpperCase() && label !== label.toLowerCase()) return label;
  return label
    .split(/(\s+)/)
    .map((word, i) => {
      if (/^\s+$/.test(word)) return word;
      const upper = word.toUpperCase();
      const known = [...KEEP_UPPER].find((k) => k.toUpperCase() === upper);
      if (known) return known;
      const lower = word.toLowerCase();
      if (i > 0 && SMALL_WORDS.has(lower)) return lower;
      return lower.charAt(0).toUpperCase() + lower.slice(1);
    })
    .join('');
}

/**
 * A subscription plan as people say it: no leading Odoo code ("[PLN-03]",
 * "P012 - "), underscores as spaces, consistent casing.
 */
export function planDisplayName(raw: string | null | undefined): string {
  if (!raw) return 'No plan';
  const cleaned = raw
    .replace(/^\s*\[[^\]]*\]\s*/, '')
    .replace(/^\s*[A-Z]{1,5}[-_]?\d{2,}\s*[-–:·|]\s*/, '')
    .replace(/_/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  const label = tidyCase(cleaned || raw.trim());
  // "3 Months Subscription" → "3 months": every plan is a subscription, so the
  // suffix is noise, and a duration reads in lower case.
  const bare = label.replace(/\s+subscription$/i, '').trim() || label;
  return bare.replace(/^(\d+\s+)(Days?|Weeks?|Months?|Years?)\b/, (_m, n: string, unit: string) => n + unit.toLowerCase());
}

const LEGAL_SUFFIX =
  /[\s,]+(ltd\.?|limited|inc\.?|llc|l\.l\.c\.|b\.?v\.?|n\.?v\.?|gmbh|ag|s\.?a\.?s\.?|s\.?a\.?|s\.?r\.?l\.?|srl|bvba|plc|llp|pbc|se|sia|corp\.?|corporation|co\.?|company|unlimited company|unlimited|pte\.?|pty\.?|oy|ab|as|aps)$/i;
const REGION_SUFFIX = /\s+(ireland|europe|emea|netherlands|belgium|germany|international|holdings?|technologies|technology|platforms)$/i;
/** A web domain standing in for the brand: "g2.com" → "g2", "Vimeo.com" → "Vimeo". */
const DOT_COM_SUFFIX = /(?<=[a-z0-9])\.com$/i;

/** Brand names kept exactly as written; the lower-case ones are the brands' own style. */
const BRAND_SPELLINGS = new Set([
  'AWS',
  'LaunchDarkly',
  'RB2B',
  'lemlist',
  '1Password',
  'Cookiebot',
  'CloudConvert',
  'Customer.io',
  'Trend Micro',
  'Lemon Squeezy',
  'n8n',
  'keystone.company',
]);

/** Payment providers whose Odoo partner carries the real merchant after them. */
const INTERMEDIARY = /^(?:spendesk|paddle\.com market ltd)\b[\s,.\-–|]+(.+)$/i;

/**
 * "HUBSPOT IRELAND LIMITED" → "Hubspot", "Google Cloud EMEA Ltd" → "Google Cloud",
 * "Google Ireland Ltd - Google Ads" → "Google". A " - Google Ads" tail that
 * repeats the brand is dropped; a payment provider's tail is the merchant and is
 * kept instead ("Paddle.com Market Ltd - Motion One" → "Motion One"). Then
 * trailing legal forms and the billing-entity region go, never the first word.
 */
export function vendorDisplayName(raw: string): string {
  let name = raw.trim();
  // Already the product's own spelling: the API's display aliases (tool-cost-mapping.ts)
  // name these, and tidying would turn "lemlist" into "Lemlist" or "n8n" into "N8n".
  if (BRAND_SPELLINGS.has(name)) return name;
  const merchant = INTERMEDIARY.exec(name);
  if (merchant?.[1]) {
    name = merchant[1].trim();
    // A lower-case merchant ("n8n") is the brand's own spelling, not a shout to tidy.
    if (name === name.toLowerCase()) return name;
  } else {
    const dash = /^(.+?)\s+[-–|]\s+(.+)$/.exec(name);
    const brandWord = (t: string) => t.trim().split(/[\s.,]+/)[0]?.toLowerCase() ?? '';
    if (dash && brandWord(dash[1]) === brandWord(dash[2])) name = dash[1].trim();
  }
  for (let i = 0; i < 4; i += 1) {
    const next = name
      .replace(LEGAL_SUFFIX, '')
      .replace(REGION_SUFFIX, '')
      .replace(DOT_COM_SUFFIX, '')
      .replace(/[\s,.-]+$/, '');
    if (next === name || next.length === 0) break;
    name = next;
  }
  // "Perplexity Ai" → "Perplexity AI": an acronym typed in title case.
  return tidyCase(name).replace(/\bAi\b/g, 'AI');
}

/**
 * Odoo's Dutch all-caps ledger names in sentence case:
 * "INFORMATICA KOSTEN - ALGEMEEN" → "Informatica kosten - algemeen". Mixed-case
 * names are left alone; known acronyms stay upper case.
 */
export function ledgerDisplayName(raw: string): string {
  const name = raw.trim();
  if (name !== name.toUpperCase()) return name;
  let first = true;
  return name
    .split(/(\s+)/)
    .map((word) => {
      if (/^\s+$/.test(word) || !/[A-Z]/.test(word)) return word;
      const known = [...KEEP_UPPER].find((k) => k.toUpperCase() === word);
      if (known) {
        first = false;
        return known;
      }
      const lower = word.toLowerCase();
      if (first) {
        first = false;
        return lower.charAt(0).toUpperCase() + lower.slice(1);
      }
      return lower;
    })
    .join('');
}

/** "12 Aug 2026" from a YYYY-MM-DD string (no timezone shift). */
export function formatDate(iso: string): string {
  return formatDayMonthYear(fromIsoDay(iso));
}

/** "4 Sep" from a YYYY-MM-DD string, for lists where the year is obvious. */
export function formatShortDate(iso: string): string {
  return formatDayMonth(fromIsoDay(iso));
}

/** Rewrites raw "2026-08" month keys inside API prose to "Aug 26", matching the headers. */
export function humanizeMonthKeys(text: string): string {
  return text.replace(/\b(\d{4})-(\d{2})\b(?!-)/g, (_m, y: string, mo: string) => formatMonthKey(`${y}-${mo}`));
}

const BILLING_CYCLE_LABEL: Record<string, string> = {
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  semiannual: 'Every 6 months',
  annual: 'Annual',
};

export function formatBillingCycle(cycle: string): string {
  return BILLING_CYCLE_LABEL[cycle] ?? cycle;
}

/**
 * Whole-unit figures for "total = part + part + …" that add up as printed.
 * Every part is rounded on its own, so it matches the same figure elsewhere on
 * the page (Outstanding €370,990), and the one at `absorb` takes the rounding
 * difference: either the total (`'total'`) or the index of a part shown
 * nowhere else. "€845,057 invoiced = €444,066 + €30,000 + €370,990" was a euro
 * out because each figure was rounded alone. Returns [total, ...parts]. Pure.
 */
export function reconcileRounded(total: number, parts: number[], absorb: 'total' | number): [number, ...number[]] {
  const rounded = parts.map((p) => Math.round(p) + 0); // + 0 turns −0 into 0
  if (absorb === 'total') return [rounded.reduce((sum, p) => sum + p, 0), ...rounded];
  const target = Math.round(total);
  const others = rounded.reduce((sum, p, i) => (i === absorb ? sum : sum + p), 0);
  rounded[absorb] = target - others;
  return [target, ...rounded];
}
